import React, { useReducer, useState, useCallback, useEffect } from "react";
import {
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    Form,
    FormGroup,
    Label,
    Input,
    Button
} from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
// helpers
import handleReducer, { textChange, onBlur, fileChange, formData } from "../lib/handleChange";
import toastAlert from "../lib/toastAlert";
import isEmpty from "../lib/isEmpty";
import fileObjectUrl from "../lib/fileOblectURL";

// api
import { workSeoEdit, getWorkSeo } from "../api/admin";
// validation
import { workSeoEditValid } from "../validation/admin.validation";
// components
import ButtonLoader from "../mycomponents/Layout/ButtonLoader";
import FieldError from "../mycomponents/Layout/FieldError";

const initialFormValue = {
    // secondaryKeywords: "",
    schemaMarkup: "",
    canonicalUrl: "",
    robotsMetaTag: "index, follow",
    ogTitle: "",
    ogDescription: "",
    ogImage: null,
};

const WorkSeoEdit = () => {
    // hooks
    const navigate = useNavigate();
    const { workId } = useParams();

    const [formValue, setFormValue] = useReducer(handleReducer, initialFormValue);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [loader, setLoader] = useState(false);

    const {
        // secondaryKeywords,
        schemaMarkup,
        canonicalUrl,
        robotsMetaTag,
        ogTitle,
        ogDescription,
        ogImage,
    } = formValue;

    const handleChange = (e) => textChange(e, setFormValue);
    const handleFileChange = (e) => fileChange(e, setFormValue);
    const handleBlur = (e) => onBlur(e, setTouched);

    const handleSubmit = useCallback(
        async (e) => {
            e.preventDefault();
            try {
                setLoader(true);
                delete formValue?.id;
                delete formValue?.name;
                const data = formData({ ...formValue, ...{ workId } });
                const { status: ok, message, errors } = await workSeoEdit(data);
                setLoader(false);

                if (ok === true) {
                    toastAlert("success", message, "blogSeoEdit");
                    setFormValue({ type: "RESET", value: initialFormValue });
                    navigate("/work-list");
                } else if (errors) {
                    setErrors(errors);
                } else if (message) {
                    toastAlert("error", message, "blogSeoEdit");
                }
            } catch (err) {
                console.log(err, "errrr");
                setLoader(false);
            }
        },
        [formValue, navigate]
    );

    const fetchData = async () => {
        try {
            let { status, result } = await getWorkSeo(workId);
            if (status == true && !isEmpty(result)) {
                setFormValue({ type: 'INITIAL_SET', value: { ...result, ...{ schemaMarkup: result.schemaMarkup == null ? "" : JSON.stringify(result.schemaMarkup) } } })
            }
        } catch (error) { }
    }

    useEffect(() => {
        const { status, errors } = workSeoEditValid({ ...formValue, ...{ workId } });
        if (!status) setErrors(errors);
        else setErrors({});
    }, [formValue]);

    useEffect(() => {
        fetchData()
    }, []);
    // console.log(errors, 'errorserrorserrors')
    return (
        <Row>
            <Col xl={12}>
                <Card>
                    <CardBody>
                        <Form>
                            <Row>
                                <Col xs={12}>
                                    <Card className="mt-3">
                                        <CardHeader className="bg-light">
                                            <strong>Advanced SEO Settings - ({formValue?.name})</strong>
                                        </CardHeader>
                                        <CardBody>
                                            <Row>
                                                {/* Left Column - SEO Fields */}
                                                <Col md={6}>
                                                    <FormGroup>
                                                        <Label>Canonical URL</Label>
                                                        <Input
                                                            type="text"
                                                            name="canonicalUrl"
                                                            value={canonicalUrl}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            placeholder="https://example.com/slug"
                                                        />
                                                        <small className="text-muted">Leave empty to auto-generate from slug</small>
                                                        <FieldError name="canonicalUrl" touched={touched} errors={errors} />
                                                    </FormGroup>

                                                    <FormGroup>
                                                        <Label>Robots Meta Tag</Label>
                                                        <Input
                                                            type="select"
                                                            name="robotsMetaTag"
                                                            value={robotsMetaTag}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                        >
                                                            <option value="index, follow">index, follow (Default)</option>
                                                            <option value="noindex, follow">noindex, follow</option>
                                                            <option value="index, nofollow">index, nofollow</option>
                                                            <option value="noindex, nofollow">noindex, nofollow</option>
                                                        </Input>
                                                        <small className="text-muted">Controls search engine indexing</small>
                                                        <FieldError name="robotsMetaTag" touched={touched} errors={errors} />
                                                    </FormGroup>

                                                    <FormGroup>
                                                        <Label>Schema Markup (JSON-LD)</Label>
                                                        <Input
                                                            type="textarea"
                                                            name="schemaMarkup"
                                                            value={schemaMarkup}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            placeholder='{"@context": "https://schema.org", "@type": "Article", "headline": "..."}'
                                                            rows={6}
                                                        />
                                                        <small className="text-muted">Paste valid JSON-LD schema or leave empty for auto-generation</small>
                                                        <FieldError name="schemaMarkup" touched={touched} errors={errors} />
                                                    </FormGroup>
                                                </Col>

                                                {/* Right Column - Open Graph Fields */}
                                                <Col md={6}>
                                                    <h6 className="mb-3">Open Graph Tags (Social Media)</h6>

                                                    <FormGroup>
                                                        <Label>OG Title</Label>
                                                        <Input
                                                            type="text"
                                                            name="ogTitle"
                                                            value={ogTitle}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            placeholder="Social media title (auto-filled from title)"
                                                        />
                                                        <small className="text-muted">Ideal: 55-60 characters</small>
                                                        <FieldError name="ogTitle" touched={touched} errors={errors} />
                                                    </FormGroup>

                                                    <FormGroup>
                                                        <Label>OG Description</Label>
                                                        <Input
                                                            type="textarea"
                                                            name="ogDescription"
                                                            value={ogDescription}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            placeholder="Social media description (auto-filled from meta description)"
                                                            rows={3}
                                                        />
                                                        <small className="text-muted">Ideal: 155-160 characters</small>
                                                        <FieldError name="ogDescription" touched={touched} errors={errors} />
                                                    </FormGroup>

                                                    <FormGroup>
                                                        <Label>OG Image (Optional)</Label>
                                                        <Input
                                                            type="file"
                                                            name="ogImage"
                                                            accept="image/*"
                                                            onChange={handleFileChange}
                                                            onBlur={handleBlur}
                                                        />
                                                        {ogImage && (
                                                            <img
                                                                src={fileObjectUrl(ogImage)}
                                                                width="140px"
                                                                alt="og preview"
                                                                className="mt-2"
                                                            />
                                                        )}
                                                        <small className="text-muted d-block mt-1">
                                                            Recommended: 1200Ã—630 px. Leave empty to use cover image.
                                                        </small>
                                                        <FieldError name="ogImage" touched={touched} errors={errors} />
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                        </CardBody>
                                    </Card>
                                </Col>
                            </Row>
                            <br />
                            <ButtonLoader
                                loader={loader}
                                color="secondary"
                                text="Save"
                                onClick={handleSubmit}
                                disabled={!isEmpty(errors)}
                            />
                        </Form>
                    </CardBody>
                </Card>
            </Col>
        </Row>
    );
};

export default WorkSeoEdit;